import { makeProductModule } from '@vs-shop/engine/module/product/makeProductModule';
import type { BackendApiErrorUnion } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { makeListProductAttributeGroups } from '../request/backendApi/product/makeListProductAttributeGroups';
import { encodeProductPathCode } from '../utils/url/encodeProductPathCode';

import {
  Product,
  ProductAttributeGroupIndex,
  ProductBase,
  ProductShow,
  ProductVariant,
  ProductVariantIndex,
  ProductVariantPriceHistoryItem,
  ProductVariantShow,
} from '../schema/openapi/backend';

const productModule = makeProductModule({
  schemas: {
    associatedProducts: ProductBase,
    productBase: ProductBase,
    productShow: ProductShow,
    productExpanded: Product,
    productsByCodes: ProductBase,
    productsByContextId: ProductBase,
    productsByTaxon: ProductBase,
    productVariantShow: ProductVariantShow,
    productVariantsByCodes: ProductVariantIndex,
    productVariantExpanded: ProductVariant,
    productVariantPriceHistory: ProductVariantPriceHistoryItem,
    searchProducts: ProductBase,
  },
  adapters: {
    productBase: { name: (product) => product.name },
    productShow: { name: (product) => product.name },
    productExpanded: { name: (product) => product.name },
    productVariantShow: { inStock: (productVariant) => productVariant.inStock },
    productVariantExpanded: { inStock: (productVariant) => productVariant.inStock },
  },
  rawUrlEncode: encodeProductPathCode,
});

export const {
  getAssociatedProducts,
  getProductExpandedByCode,
  getProductsByCodes,
  requestProductsByContextId,
  getProductsByTaxon,
  getProductVariantByCode,
  listProductVariantByCodes,
  getProductVariantExpandedByCode,
  getProductVariantPriceHistory,
  requestSearchProducts,
  GetAssociatedProductsError,
  GetProductExpandedByCodeError,
  GetProductsByCodesError,
  GetProductsByContextIdError,
  GetProductsByTaxonError,
  GetProductVariantByCodeError,
  ListProductVariantByCodesError,
  GetProductVariantExpandedByCodeError,
  GetProductVariantPriceHistoryError,
  GetSearchProductsError,
} = productModule;

// Attribute groups are rcprofi-only: RacingLine's backend serves no
// `/api/v2/shop/product-attribute-group`, so this request module lives in this
// package and is bound here rather than in the shared engine product module.
const attributeGroupsModule = makeListProductAttributeGroups({
  schemas: { productAttributeGroups: ProductAttributeGroupIndex },
});

export const requestProductAttributeGroups = attributeGroupsModule.requestProductAttributeGroups();
export const ListProductAttributeGroupsError =
  attributeGroupsModule.ListProductAttributeGroupsError;
export type ProductAttributeGroupListError = BackendApiErrorUnion<
  typeof attributeGroupsModule.ListProductAttributeGroupsError
>;

export type {
  ProductExpandedError,
  ProductGetAssociatedProductsError,
  ProductGetProductVariantExpandedByCodeError,
  ProductGetProductsByCodesError,
  ProductGetProductsByContextIdError,
  ProductGetProductsByTaxonError,
  ProductVariantGetByCodeError,
  ProductVariantGetPriceHistoryError,
  ProductVariantListByCodesError,
  SearchGetSearchProductsError,
} from '@vs-shop/engine/module/product/makeProductModule';
